import { useNavigate, useRouter } from "@tanstack/react-router"
import * as React from "react"
import clsx from "~/sdk/clx"
import { prefetchUrl } from "~/components/ui/PrefetchAnchor"
import stripTrackingParams from "~/sdk/stripTrackingParams"

/**
 * Mesmo intent-delay do PrefetchAnchor: o cursor precisa descansar no thumb
 * antes de disparar o preload — varrer a fileira de variantes com o mouse não
 * pode virar um /_serverFn por thumbnail.
 */
const HOVER_DELAY_MS = 80

/**
 * `search: (prev) => ({ ...prev, skuId })` herdaria a query INTEIRA da entrada —
 * inclusive `?gclid`/`?gad_source` de um clique de Ads, únicos por visita. Como o
 * router casa rota por search completo (`ignoreSearchParams: []`) com
 * `staleTime: Infinity`, cada variante clicada viraria uma entrada nova no cache
 * do router E um payload novo no `/_serverFn` (fragmenta Worker e CDN). Filtra o
 * `prev` pela mesma denylist de href/canonical/edge antes do spread; params
 * funcionais (map, O, q, page…) sobrevivem por definição da lista.
 */
function withoutTracking(prev: Record<string, unknown>): Record<string, unknown> {
    const params = new URLSearchParams()
    for (const key of Object.keys(prev)) params.set(key, "")
    stripTrackingParams(params)
    return Object.fromEntries(
        Object.keys(prev)
            .filter((key) => params.has(key))
            .map((key) => [key, prev[key]]),
    )
}

/**
 * Never use the VTEX leaf url raw as href — its origin is the literal
 * `https://https/`. Keep pathname + search only, and pull out the `skuId` that
 * identifies the variant.
 */
export function parseVariantUrl(url?: string): { href?: string; skuId?: string } {
    if (!url) return {}
    try {
        const parsed = new URL(url)
        return {
            href: parsed.pathname + parsed.search,
            skuId: parsed.searchParams.get('skuId') ?? undefined,
        }
    } catch {
        return {} // unparseable url → dead link
    }
}

/**
 * Navega para uma variante (só o `?skuId=` muda) sem recarregar o documento.
 *
 * Fora do `VariantLink` isto serve à válvula de escape do estado esgotado: com a
 * SKU atual sem estoque o buy box vira o formulário "avise-me", então mexer na
 * quantidade de um tamanho DISPONÍVEL precisa trazer a página para aquela SKU
 * para o CTA de compra voltar. No Fresh era o `f-partial` condicional dos
 * botões `-`/`+` (MultiSelector/SimpleSelector, prop `isCurrentProductAvailable`).
 */
export function useVariantNavigate() {
    const navigate = useNavigate()

    return React.useCallback((url?: string) => {
        const { href, skuId } = parseVariantUrl(url)
        if (!href) return Promise.resolve()
        if (!skuId) {
            window.location.assign(href)
            return Promise.resolve()
        }
        return navigate({
            to: '.',
            search: (prev) => ({ ...withoutTracking(prev), skuId }),
            resetScroll: false,
        })
    }, [navigate])
}

export interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    /** Raw VTEX variant URL (comes with a broken `https://https/` origin). */
    url?: string
}

/**
 * Variant link on the PDP (DECO-5445). In the original Fresh app this was a
 * `<button f-partial={url} f-client-nav>` — a partial navigation that
 * re-rendered the PDP with the clicked SKU's images. Here it becomes a
 * TanStack SPA navigation: only `?skuId=` changes, the route loader re-runs
 * (the VTEX page loader honors skuId and returns that SKU's images/offers)
 * and the gallery swaps without a full document reload. Requires `skuId` NOT
 * to be in `ignoreSearchParams` (src/routes/$.tsx), otherwise the loader
 * never sees the new SKU on client navigations.
 */
export default function VariantLink({ url, onClick, className, children, ...rest }: Props) {
    const navigateToVariant = useVariantNavigate()
    const router = useRouter()
    // True while the SPA navigation/loader triggered by this link is in
    // flight — drives the spinner overlay on the clicked thumbnail.
    const [pending, setPending] = React.useState(false)
    const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

    const { href, skuId } = parseVariantUrl(url)

    // Prefetch no hover/focus/touch. O `defaultPreload: "intent"` do router só
    // vale para <Link> — este é um <a> cru com navigate() manual, então sem
    // isto o hover não esquentava NADA e o clique pagava o round-trip inteiro
    // do /_serverFn (o loader resolve a página toda no servidor).
    //
    // `router.preloadRoute` é o warm certo aqui (e não o fetch de documento do
    // PrefetchAnchor): o clique é navegação SPA, consome o loader do router.
    // O preload usa o MESMO search do clique (withoutTracking + skuId), então a
    // entrada esquentada é exatamente a consumida — com staleTime Infinity em
    // prod, o clique vira troca instantânea. Preloads repetidos no mesmo alvo
    // são no-op (preloadRoute curto-circuita em cachedMatches).
    //
    // No fallback raro sem skuId (full-page nav via location.assign), o warm
    // certo é o documento — aí sim PrefetchAnchor.prefetchUrl.
    const schedulePrefetch = () => {
        if (hoverTimer.current || pending) return
        hoverTimer.current = setTimeout(() => {
            hoverTimer.current = null
            if (skuId) {
                void router
                    .preloadRoute({
                        to: '.',
                        search: (prev: Record<string, unknown>) => ({
                            ...withoutTracking(prev),
                            skuId,
                        }),
                    } as Parameters<typeof router.preloadRoute>[0])
                    .catch(() => {
                        // Preload é oportunista: falha silenciosa, o clique refaz.
                    })
            } else if (href) {
                prefetchUrl(href)
            }
        }, HOVER_DELAY_MS)
    }
    const cancelPrefetch = () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current)
            hoverTimer.current = null
        }
    }

    return (
        <a
            href={href ?? '#'}
            onMouseEnter={schedulePrefetch}
            onMouseLeave={cancelPrefetch}
            onFocus={schedulePrefetch}
            onBlur={cancelPrefetch}
            onTouchStart={schedulePrefetch}
            onClick={(e) => {
                onClick?.(e)
                if (!href) {
                    e.preventDefault()
                    return
                }
                // Modified/middle clicks keep native behavior (new tab etc.)
                if (
                    e.defaultPrevented ||
                    e.button !== 0 ||
                    e.metaKey ||
                    e.ctrlKey ||
                    e.shiftKey ||
                    e.altKey
                ) {
                    return
                }
                e.preventDefault()
                cancelPrefetch()
                // Variants share the pathname; only skuId changes.
                if (skuId) setPending(true)
                void navigateToVariant(url).finally(() => setPending(false))
            }}
            className={clsx('relative', pending && 'cursor-wait', className)}
            {...rest}
        >
            {children}
            {pending && (
                <span className='absolute inset-0 z-[1] grid place-items-center bg-white/60'>
                    <span className='loading loading-spinner loading-md text-salmon' />
                </span>
            )}
        </a>
    )
}
